import { Alert, Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import Card from '~/components/Commons/Card/Card';
import CardBody from '~/components/Commons/Card/CardBody';
import type { AnalyticsStatMetadata } from '~/hooks/queries/useAnalyticsStatMetadataQuery';
import { getAnalyticsStatDownloadErrorMessage } from './analyticsStatsErrorUtils';

const sectionLabelSx = {
  py: 1,
  px: 1.5,
  borderLeft: '4px solid',
  borderColor: 'dianomiGreen.main',
  bgcolor: 'grey.100',
};

type StateCardProps = {
  title: string;
  children: ReactNode;
};

const StateCard = ({ title, children }: StateCardProps) => (
  <Card>
    <Box component="header" sx={{ ...sectionLabelSx, mx: 2, mt: 2 }}>
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
    </Box>
    <CardBody sx={{ m: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>{children}</CardBody>
  </Card>
);

type NoSearchMatchCardProps = {
  title?: string;
  body?: string;
};

export const NoSearchMatchCard = ({
  title = 'No results match your search',
  body = 'Try a different search term or clear the search.',
}: NoSearchMatchCardProps = {}) => (
  <StateCard title={title}>
    <Typography variant="body1" component="p">
      {body}
    </Typography>
  </StateCard>
);

export const NoViewsCard = () => (
  <StateCard title="No reports found">
    <Typography variant="body1" component="p">
      No reports have been found for this account.
    </Typography>
  </StateCard>
);

export const MisConfiguredStateCard = () => (
  <StateCard title="Stats not configured for this account">
    <Typography variant="body1" component="p">
      No stats have been set up for this account yet. Stats should be configured by your account
      manager before you can run reports or create saved reports.
    </Typography>
    <Typography variant="body1" component="p">
      Please contact your account manager to have stats enabled for this account.
    </Typography>
  </StateCard>
);

export const ErrorStateCard = () => (
  <StateCard title="Unable to load reports">
    <Typography variant="body1">
      There was an error loading available reports. Please refresh and try again.
    </Typography>
  </StateCard>
);

/** Async stat job is queued or running; poll has not reached a terminal state yet. */
export const ASYNC_STAT_QUEUED_OR_RUNNING_MESSAGE =
  'This report is queued and will load here when it is ready. Stay on this page to see the results as soon as they are available. If you leave before it finishes, you will receive an email with a link to view the report, or with an attachment if you requested a download.';

/** Async file download job is queued or running. */
export const ASYNC_DOWNLOAD_QUEUED_OR_RUNNING_MESSAGE =
  'Your file download is queued and will start here when it is ready. Stay on this page to save the file as soon as it is available. If you leave before it finishes, you will receive an email with a link to the report, or with an attachment if you requested a download.';

type AnalyticsStatDownloadErrorAlertProps = {
  error: unknown;
  metadata?: AnalyticsStatMetadata;
  onDismiss: () => void;
};

/** Inline alert for failed stat report downloads (POST, poll, or presigned blob). */
export const AnalyticsStatDownloadErrorAlert = ({
  error,
  metadata,
  onDismiss,
}: AnalyticsStatDownloadErrorAlertProps) => (
  <Alert severity="error" onClose={onDismiss}>
    {getAnalyticsStatDownloadErrorMessage(error, metadata)}
  </Alert>
);
